import { useCallback, useEffect, useState } from "react";
import { singletonHook } from "react-singleton-hook";

// Media query standard: https://stackoverflow.com/questions/6370690/media-queries-how-to-target-desktop-tablet-and-mobile
// @media (min-width:320px)  { /* smartphones, iPhone, portrait 480x320 phones */ }
// @media (min-width:481px)  { /* portrait e-readers (Nook/Kindle), smaller tablets @ 600 or @ 640 wide. */ }
// @media (min-width:641px)  { /* portrait tablets, portrait iPad, landscape e-readers, landscape 800x480 or 854x480 phones */ }
// @media (min-width:961px)  { /* tablet, landscape iPad, lo-res laptops ands desktops */ }
// @media (min-width:1025px) { /* big landscape tablets, laptops, and desktops */ }
// @media (min-width:1281px) { /* hi-res laptops and desktops */ }

export enum DeviceType {
  Desktop = "Desktop",
  Tablet = "Tablet",
  Mobile = "Mobile",
}

export const LARGE_DESKTOP_WIDTH = 1400;
export const MID_DESKTOP_WIDTH = 1200;
export const TABLET_WIDTH = 1024;
export const MOBILE_WIDTH = 480;

export const MAX_WIDTH_MOBILE = 480;
export const MAX_WIDTH_TABLET = 1024;
export const MIN_WIDTH_DESKTOP = 1024;

const useDeviceTypeImpl = (): DeviceType => {
  const [width, setWidth] = useState(window.innerWidth);

  const handleWindowSizeChange = useCallback(() => {
    setWidth(window.innerWidth);
  }, []);

  useEffect(() => {
    window.addEventListener("resize", handleWindowSizeChange);
    return () => {
      window.removeEventListener("resize", handleWindowSizeChange);
    };
  }, [handleWindowSizeChange]);

  if (width <= MOBILE_WIDTH) {
    return DeviceType.Mobile;
  }
  if (width <= TABLET_WIDTH) {
    return DeviceType.Tablet;
  }
  return DeviceType.Desktop;
};

export const useDeviceType = singletonHook(getDeviceType(), useDeviceTypeImpl);

// Helpers
export const useIsMobileOrTablet = (): boolean => {
  return useDeviceType() !== DeviceType.Desktop;
};
export const useIsMobile = (): boolean => {
  return useDeviceType() === DeviceType.Mobile;
};

export const useIsTablet = (): boolean => {
  return useDeviceType() === DeviceType.Tablet;
};

export const useIsDesktop = (): boolean => {
  return useDeviceType() === DeviceType.Desktop;
};

export function getDeviceType() {
  const width = window.innerWidth;
  if (width <= MAX_WIDTH_MOBILE) {
    return DeviceType.Mobile;
  }
  if (width <= MAX_WIDTH_TABLET) {
    return DeviceType.Tablet;
  }
  return DeviceType.Desktop;
}
